import { useState, useEffect } from "react";
import { X, Minus, Plus } from "lucide-react";
import { message } from "antd";
import foodApiService, { EditFoodItemPayload } from "../../../services/foodApiService";
import { buildEditFoodPayload } from "../../../utils/foodEditPayload";
import { cn } from "../../../lib/utils";
import {
  gluceraFieldHint,
  gluceraFieldInput,
  gluceraFieldLabel,
  gluceraFormControl,
  gluceraFormMetric,
  gluceraSheetButtonPrimary,
  gluceraSheetButtonSecondary,
  gluceraSheetTitle,
} from "../../../utils/gluceraTypography";

interface FoodItem {
  id: number;
  food_item_id?: string;
  name: string;
  calories: number;
  weight: number;
  servings: number;
  count?: number;
  predicted_count?: number;
  predicted_serving_size_g?: number;
  predicted_serving_description?: string;
  unit_label?: string;
  predicted_unit_label?: string;
  image: string;
  carbohydrate_content_g?: number;
  protein_content_g?: number;
  fat_content_g?: number;
  dietary_fiber_g?: number;
  net_carbs_g?: number;
  sugar_g?: number;
  trans_fat_g?: number;
  saturated_fat_g?: number;
  GI_estimate?: number;
  GL_estimate?: number;
  serving_description?: string;
}

interface Props {
  foodItem: FoodItem;
  onClose: () => void;
  onSave: (updatedItem: FoodItem) => void;
}

const EditFoodModal = ({ foodItem, onClose, onSave }: Props) => {
  const [editedData, setEditedData] = useState({
    name: foodItem.name,
    servingDescription: foodItem.serving_description ?? "",
    servings: foodItem.servings,
    weight: foodItem.weight,
    calories: foodItem.calories,
  });

  const parseFromDesc = (desc?: string) => {
    if (!desc) return 1;
    const match = desc.match(/^(\d+(?:\.\d+)?)\s/);
    const parsed = match ? parseFloat(match[1]) : 1;
    return parsed > 0 ? parsed : 1;
  };

  const [servingMultiplier, setServingMultiplier] = useState<number>(() => {
    if (foodItem.count && foodItem.count > 0) return foodItem.count;
    return foodItem.servings && foodItem.servings > 0
      ? foodItem.servings
      : parseFromDesc(foodItem.serving_description);
  });

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setEditedData({
      name: foodItem.name,
      servingDescription: foodItem.serving_description ?? "",
      servings: foodItem.servings,
      weight: foodItem.weight,
      calories: foodItem.calories,
    });
    const initial =
      foodItem.count && foodItem.count > 0
        ? foodItem.count
        : foodItem.servings && foodItem.servings > 0
        ? foodItem.servings
        : parseFromDesc(foodItem.serving_description);
    setServingMultiplier(initial);
  }, [foodItem]);

  const handleDecrement = () => {
    setServingMultiplier(prev => Math.max(0.5, parseFloat((prev - 0.5).toFixed(1))));
  };

  const handleIncrement = () => {
    setServingMultiplier(prev => Math.min(20, parseFloat((prev + 0.5).toFixed(1))));
  };

  const unitLabel =
    foodItem.predicted_unit_label ??
    foodItem.unit_label ??
    (foodItem.predicted_serving_description ??
      editedData.servingDescription ??
      foodItem.serving_description ??
      `${foodItem.weight}g`)
      .replace(/^\d+(\.\d+)?x?\s*/i, "")
      .replace(/^\d+(\.\d+)?\s*/, "");

  const handleSave = async () => {
    if (!editedData.name.trim()) {
      message.error("Please enter food name");
      return;
    }

    setIsLoading(true);
    try {
      const apiPayload: EditFoodItemPayload = buildEditFoodPayload(
        foodItem,
        editedData.name,
        servingMultiplier
      );

      const response = await foodApiService.editManualFood(apiPayload);
      const responseData: any = (response.data as any)?.item ?? (response.data as any)?.data ?? response.data;
      const responseCount = responseData?.count ?? servingMultiplier;

      const updatedFoodItem: FoodItem = {
        ...foodItem,
        name: responseData?.name ?? editedData.name.trim(),
        serving_description: responseData?.serving_description ?? foodItem.serving_description,
        count: responseCount,
        servings: responseCount,
        weight: responseData?.serving_size_g ?? foodItem.weight,
        calories: responseData?.calorie_content_kcal ?? responseData?.calories ?? foodItem.calories,
        protein_content_g: responseData?.protein_content_g ?? foodItem.protein_content_g,
        carbohydrate_content_g: responseData?.carbohydrate_content_g ?? foodItem.carbohydrate_content_g,
        net_carbs_g: responseData?.net_carbs_g ?? foodItem.net_carbs_g,
        fat_content_g: responseData?.fat_content_g ?? foodItem.fat_content_g,
        trans_fat_g: responseData?.trans_fat_g ?? foodItem.trans_fat_g,
        saturated_fat_g: responseData?.saturated_fat_g ?? foodItem.saturated_fat_g,
        sugar_g: responseData?.sugar_g ?? foodItem.sugar_g,
        dietary_fiber_g: responseData?.dietary_fiber_g ?? foodItem.dietary_fiber_g,
        GI_estimate: responseData?.GI_estimate ?? foodItem.GI_estimate,
        GL_estimate: responseData?.GL_estimate ?? foodItem.GL_estimate,
      };

      onSave(updatedFoodItem);
      onClose();
    } catch (error: any) {
      message.error(error.message || "Failed to update food item");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-[400px] rounded-t-2xl bg-card shadow-xl">
        <div className="flex justify-center pt-3 pb-1">
          <div className="h-1 w-10 rounded-full bg-border" />
        </div>

        <div className="max-h-[85dvh] overflow-y-auto px-4 pb-[env(safe-area-inset-bottom,16px)]" style={{paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 24px)'}}>
          <div className="mb-4 flex items-center justify-between py-2">
            <h2 className={gluceraSheetTitle}>Edit Food Item</h2>
            <button
              onClick={onClose}
              className="rounded-full p-1.5 transition-colors hover:bg-muted"
            >
              <X className="h-5 w-5 text-muted-foreground" />
            </button>
          </div>

          <div className="mb-4 space-y-1.5">
            <label className={gluceraFieldLabel}>Food Name</label>
            <input
              type="text"
              value={editedData.name}
              onChange={e => {
                const val = e.target.value.replace(/[^\p{L}\p{N}\s\-'.,()&/]/gu, "");
                setEditedData({ ...editedData, name: val });
              }}
              className={gluceraFormControl}
              placeholder="Enter food name"
              maxLength={100}
            />
          </div>

          <div className="mb-4 space-y-1.5">
            <label className={gluceraFieldLabel}>Serving Size</label>
            <div className="flex gap-3">
              <div className="w-[130px]">
                <span className={gluceraFieldHint}>Qty</span>
              </div>
              <div className="flex flex-1 items-center justify-between">
                <span className={gluceraFieldHint}>Unit</span>
                <span className={cn(gluceraFieldHint, "text-muted-foreground/70")}>(read only)</span>
              </div>
            </div>
            <div className="flex items-stretch gap-3">
              <div className="flex w-[130px] items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2">
                <button
                  type="button"
                  onClick={handleDecrement}
                  disabled={servingMultiplier <= 0.5}
                  className="flex h-7 w-7 items-center justify-center rounded-md bg-card shadow-sm transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Minus className="h-4 w-4 text-muted-foreground" />
                </button>
                <span className={cn("w-8 text-center", gluceraFormMetric)}>
                  {servingMultiplier}
                </span>
                <button
                  type="button"
                  onClick={handleIncrement}
                  disabled={servingMultiplier >= 20}
                  className="flex h-7 w-7 items-center justify-center rounded-md bg-card shadow-sm transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Plus className="h-4 w-4 text-muted-foreground" />
                </button>
              </div>
              <div className={cn("flex flex-1 items-center rounded-lg border border-border bg-muted/40 px-4 py-3", gluceraFieldInput)}>
                {unitLabel}
              </div>
            </div>
            <p className={cn(gluceraFieldHint, "text-muted-foreground/70")}>Adjust servings (0.5 to 20)</p>
          </div>

          <div className="space-y-3">
            <button
              onClick={handleSave}
              disabled={isLoading}
              className={cn("w-full rounded-lg bg-gradient-primary py-3 text-primary-foreground shadow-primary transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50", gluceraSheetButtonPrimary)}
            >
              {isLoading ? "Saving Changes..." : "Save Changes"}
            </button>
            <button
              onClick={onClose}
              className={cn("w-full rounded-lg border border-border bg-card py-3 text-foreground transition-colors hover:bg-muted", gluceraSheetButtonSecondary)}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditFoodModal;
